import * as React from 'react';

/**
 * Categorical palette — fixed order, validated for colour-vision deficiency on a
 * light surface. Never cycled: a 9th series must fold into "Other".
 */
export const CHART_COLORS = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
  '#4a3aa7',
  '#e34948',
] as const;

/** Single-hue ramp for magnitude (heatmaps). Light → dark. */
export const SEQUENTIAL_BLUE = [
  '#cde2fb',
  '#b7d3f6',
  '#9ec5f4',
  '#86b6ef',
  '#6da7ec',
  '#5598e7',
  '#3987e5',
  '#2a78d6',
  '#256abf',
  '#1c5cab',
  '#184f95',
  '#104281',
  '#0d366b',
] as const;

/** Text / chrome tokens shared by every chart (light surface). */
export const CHART_INK = {
  primary: '#0b0b0b',
  secondary: '#52514e',
  muted: '#8a8984',
  grid: '#e8e9ed',
  track: '#eef0f3',
  surface: '#ffffff',
} as const;

export type ChartSeries = {
  key: string;
  label: string;
  color?: string;
  /** Draw as a dashed line (comparison / reference series). Bar charts ignore it. */
  dashed?: boolean;
};

/** `bucket` is an ISO date (`yyyy-mm-dd`); series values live under their keys. */
export type TimePoint = { bucket: string } & Record<string, number | string>;

/** Resolve a colour per series, in fixed slot order. Warns and clamps past 8. */
export function resolveSeriesColors(series: ChartSeries[]): string[] {
  if (series.length > CHART_COLORS.length) {
    console.warn(
      `Charts support at most ${CHART_COLORS.length} series; got ${series.length}. Fold the tail into "Other".`,
    );
  }
  return series.map(
    (s, i) => s.color ?? CHART_COLORS[Math.min(i, CHART_COLORS.length - 1)],
  );
}

export function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${trimZero((abs / 1_000_000).toFixed(1))}M`;
  if (abs >= 10_000) return `${sign}${trimZero((abs / 1_000).toFixed(1))}K`;
  if (Number.isInteger(n)) return n.toLocaleString('en-US');
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

function trimZero(s: string): string {
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}

export function formatPercent(n: number, digits = 0): string {
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(digits)}%`;
}

/** Clean tick values from 0 up to at least `max` (e.g. 0, 50, 100, 150). */
export function niceTicks(max: number, count = 4): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  const rough = max / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const residual = rough / magnitude;
  let niceStep: number;
  if (residual <= 1) niceStep = 1;
  else if (residual <= 2) niceStep = 2;
  else if (residual <= 2.5) niceStep = 2.5;
  else if (residual <= 5) niceStep = 5;
  else niceStep = 10;
  const step = niceStep * magnitude;
  const ticks: number[] = [];
  for (let v = 0; v < max + step; v += step) {
    ticks.push(Number(v.toFixed(10)));
    if (v >= max) break;
  }
  return ticks;
}

export type Measured = { width: number; height: number };

const FALLBACK: Measured = { width: 600, height: 300 };

/**
 * Observes the element's size. Falls back to 600×300 where ResizeObserver is
 * missing (jsdom) so charts still render in tests.
 */
export function useMeasure<T extends HTMLElement = HTMLDivElement>(): [
  React.RefObject<T>,
  Measured,
] {
  const ref = React.useRef<T>(null);
  const [size, setSize] = React.useState<Measured>(FALLBACK);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) setSize({ width: rect.width, height: rect.height });
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, size];
}

/** Picks every Nth label so x-axis labels never overlap. */
export function labelStride(count: number, plotWidth: number, labelWidth = 44): number {
  if (count <= 0) return 1;
  const maxLabels = Math.max(1, Math.floor(plotWidth / labelWidth));
  return Math.max(1, Math.ceil(count / maxLabels));
}

export function defaultXLabel(bucket: string): string {
  // yyyy-mm-dd → "dd/mm"; anything else is shown as-is.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(bucket);
  if (!m) return bucket;
  return `${m[3]}/${m[2]}`;
}

export function seriesValue(point: TimePoint, key: string): number {
  const v = point[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** Relative luminance of a hex colour (0 = black, 1 = white). */
export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Maps a 0..100 value onto the sequential blue ramp. */
export function sequentialColor(value: number): string {
  const clamped = Math.max(0, Math.min(100, value));
  const idx = Math.round((clamped / 100) * (SEQUENTIAL_BLUE.length - 1));
  return SEQUENTIAL_BLUE[idx];
}

export const CHART_MARGIN = { top: 12, right: 12, bottom: 28, left: 44 } as const;
